import { useEffect, useState } from 'react'
import { api, can, type ContactHistory } from '../lib/api'

/** A caller's past calls, one row each, WITH HOW EACH ONE ENDED.
 *
 *  The customer's reason for the Caller history tick: while a call comes in,
 *  a trusted dispatcher sees the caller's past "to know how to interact with
 *  that person" -- and that is the closing reason and the closing note, not
 *  the nature and the date alone. Drawn by the ringing caller card and by the
 *  Take a call box, from one component so the two cannot say different
 *  things about the same person. */
export function PastCallRows({
  incidents, limit = 6,
}: {
  incidents: ContactHistory['incidents']
  limit?: number
}) {
  return (
    <>
      {incidents.slice(0, limit).map((i) => (
        <div className="pastcall" key={i.id}>
          <div className="listrow sub">
            <span className="mono">{i.number}</span>
            <span className="nm">{i.nature_label}</span>
            {i.location && <span className="dim">{i.location}</span>}
            <span className="dim">{new Date(i.called_at).toLocaleDateString()}</span>
            {i.status !== 'closed'
              ? <span className="pill wait">still open</span>
              : i.resolution_label && <span className="tag">{i.resolution_label}</span>}
          </div>
          {i.outcome_note && <div className="ended">“{i.outcome_note}”</div>}
        </div>
      ))}
      {incidents.length > limit && (
        <div className="dim more">and {incidents.length - limit} earlier</div>
      )}
    </>
  )
}

/** The same list in the Take a call box, for a call started by hand.
 *
 *  THE SAME TICK AS THE RINGING CARD (`calls.incident_record.caller_history`):
 *  without it nothing is asked for and nothing is drawn. The number is looked
 *  up the way the caller-ID pop looks it up, and a SHARED LINE shows nothing --
 *  two people on one number have two histories and there is no telling whose
 *  this caller is. Never in the way: a failure draws nothing, because taking
 *  the call is what matters. */
export function CallerPastCalls({ phone }: { phone: string }) {
  const allowed = can('calls.incident_record.caller_history')
  const [history, setHistory] = useState<ContactHistory | null>(null)

  useEffect(() => {
    setHistory(null)
    const digits = phone.replace(/\D/g, '')
    if (!allowed || digits.length < 7) return
    let alive = true
    const timer = setTimeout(async () => {
      try {
        // ALWAYS BY THE NUMBER, EVEN WHEN THE POP HANDED A CONTACT. The pop's
        // contact on a shared line is simply whoever was edited last, and
        // showing HIS closing notes while his wife is on the phone is the
        // wrong-person failure the shared-line rule exists to stop.
        const found = await api.contactLookup(phone)
        if (!alive || !found.match || (found.holders ?? []).length > 1) return
        const h = await api.contactHistory(found.match.id)
        if (alive) setHistory(h)
      } catch {
        // Help, never a blocker.
      }
    }, 450)
    return () => { alive = false; clearTimeout(timer) }
  }, [phone, allowed])

  if (!allowed || !history || history.incidents.length === 0) return null
  return (
    <div className="callerpast">
      <div className="lbl">Past calls from this caller · {history.incidents.length}</div>
      <PastCallRows incidents={history.incidents} limit={5} />
    </div>
  )
}
